import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export interface TextEditRef {
  startTextEditForDomUniquePath: (domUniquePath?: string) => void;
}

interface PendingTextEditRequest {
  storyboardId: string;
  domUniquePath?: string;
}

interface TextEditStore {
  // Registry of text edit refs by storyboard ID
  textEditRefs: Map<string, TextEditRef>;

  // Pending requests waiting for refs to be registered
  pendingRequests: Map<string, PendingTextEditRequest>;

  // Register a text edit ref for a storyboard
  registerTextEditRef: (storyboardId: string, ref: TextEditRef) => void;

  // Unregister a text edit ref when component unmounts
  unregisterTextEditRef: (storyboardId: string) => void;

  // Request to start text edit for a storyboard
  requestTextEdit: (storyboardId: string, domUniquePath?: string) => void;

  // Get text edit ref for a storyboard
  getTextEditRef: (storyboardId: string) => TextEditRef | undefined;
}

const PENDING_REQUEST_TIMEOUT = 60 * 1000; // 1 minute timeout for pending requests

export const useTextEditStore = create<TextEditStore>()(
  subscribeWithSelector((set, get) => ({
    textEditRefs: new Map(),
    pendingRequests: new Map(),

    registerTextEditRef: (storyboardId: string, ref: TextEditRef) => {
      set((state) => {
        const newRefs = new Map(state.textEditRefs);
        newRefs.set(storyboardId, ref);

        // Check if there's a pending request for this storyboard
        const pendingRequest = state.pendingRequests.get(storyboardId);
        if (pendingRequest) {
          // Execute the pending request
          setTimeout(() => {
            ref.startTextEditForDomUniquePath(pendingRequest.domUniquePath);
          }, 0);

          // Remove the pending request
          const newPendingRequests = new Map(state.pendingRequests);
          newPendingRequests.delete(storyboardId);

          return {
            textEditRefs: newRefs,
            pendingRequests: newPendingRequests,
          };
        }

        return { textEditRefs: newRefs };
      });
    },

    unregisterTextEditRef: (storyboardId: string) => {
      set((state) => {
        const newRefs = new Map(state.textEditRefs);
        newRefs.delete(storyboardId);
        return { textEditRefs: newRefs };
      });
    },

    requestTextEdit: (storyboardId: string, domUniquePath?: string) => {
      const state = get();
      const ref = state.textEditRefs.get(storyboardId);

      if (ref) {
        // Ref exists, start editing immediately
        ref.startTextEditForDomUniquePath(domUniquePath);
      } else {
        set((state) => {
          const newPendingRequests = new Map(state.pendingRequests);
          newPendingRequests.set(storyboardId, {
            storyboardId,
            domUniquePath,
          });
          return { pendingRequests: newPendingRequests };
        });

        // Set up a subscription to watch for the ref being registered
        const unsubscribe = useTextEditStore.subscribe(
          (state) => state.textEditRefs,
          (textEditRefs) => {
            const ref = textEditRefs.get(storyboardId);
            if (ref) {
              // Ref is now available, unsubscribe and let the registerTextEditRef handle it
              unsubscribe();
            }
          },
        );

        // Clean up subscription after timeout
        setTimeout(() => {
          unsubscribe();
          set((state) => {
            const newPendingRequests = new Map(state.pendingRequests);
            newPendingRequests.delete(storyboardId);
            return { pendingRequests: newPendingRequests };
          });
        }, PENDING_REQUEST_TIMEOUT);
      }
    },

    getTextEditRef: (storyboardId: string) => {
      return get().textEditRefs.get(storyboardId);
    },
  })),
);
